import { api } from './api';

export type OpenSite = {
  id: string;
  name: string;
  project_id: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  last_inspection_date: string | null;
  next_inspection_date: string | null;
  days_overdue: number | null;
  is_overdue: boolean;
  inspection_frequency: string;
  /** JIM-0929-12: claimed within the last week, listed even when not due. */
  just_claimed?: boolean;
  needs_inspection?: boolean;
  // Additional fields for unclaimed sites
  client_name?: string | null;
  distance_miles?: number;
};

export type InspectorHome = {
  latitude: number;
  longitude: number;
  address: string | null;
};

export type OpenSitesResponse = {
  items: OpenSite[];
  total: number;
  inspector_home: InspectorHome | null;
};

export type RouteSegment = {
  from_name: string;
  to_name: string;
  to_site_id: string | null;
  distance_miles: number;
  travel_minutes: number;
};

export type OptimizedRoute = {
  optimized_order: string[];
  total_distance_miles: number;
  total_travel_minutes: number;
  segments: RouteSegment[];
  savings: {
    distance_saved: number;
    time_saved: number;
    percentage_improvement: number;
  };
};

export type BulkScheduleResult = {
  message: string;
  scheduled_date: string;
  inspections: Array<{
    id: string;
    site_id: string;
    site_name: string;
    scheduled_date: string;
    type: string;
    status: string;
    route_order: number;
  }>;
};

export type ClaimSiteResult = {
  message: string;
  site_id: string;
  site_name: string;
  inspector_id: string;
  inspector_name: string;
};

export type MyAccessSite = {
  id: string;
  name: string;
  /** The customer beside the name (entity override, else the client). */
  client_name?: string | null;
  status: string;
  city: string | null;
  state: string | null;
  transition_phase?: string | null;
  in_group?: boolean;
  last_inspection_date?: string | null;
  next_inspection_date?: string | null;
  days_overdue?: number | null;
  via_groups?: string[];
  latitude?: number | null;
  longitude?: number | null;
};

export type MyAccessResponse = {
  groups: string[];
  my_sites: MyAccessSite[];
  group_sites: MyAccessSite[];
  // Overdue (day-based) + rainfall (a rain event inspection still to do) buckets.
  // A site can appear here AND in my_sites — the landing renders one section each.
  overdue: MyAccessSite[];
  rainfall: MyAccessSite[];
};

class ScheduleService {
  /**
   * Get sites assigned to the current inspector that need inspection
   */
  async getMyOpenSites(): Promise<OpenSitesResponse> {
    const response = await api.get('/sites/my-assigned-open');
    return response.data as OpenSitesResponse;
  }

  /**
   * Optimize route for visiting multiple sites
   */
  async optimizeRoute(
    siteIds: string[],
    startLat: number,
    startLon: number,
    returnToStart: boolean = true
  ): Promise<OptimizedRoute> {
    const response = await api.post('/scheduling/optimize-route', {
      site_ids: siteIds,
      start_latitude: startLat,
      start_longitude: startLon,
      return_to_start: returnToStart,
    });
    return response.data as OptimizedRoute;
  }

  /**
   * Bulk schedule inspections for multiple sites
   */
  async bulkScheduleInspections(
    siteIds: string[],
    scheduledDate: string,
    inspectionType: string = 'routine'
  ): Promise<BulkScheduleResult> {
    const response = await api.post('/scheduling/bulk-schedule', {
      site_ids: siteIds,
      scheduled_date: scheduledDate,
      inspection_type: inspectionType,
    });
    return response.data as BulkScheduleResult;
  }

  /**
   * Update the current user's home location
   */
  async updateHomeLocation(
    latitude: number,
    longitude: number,
    address?: string
  ): Promise<void> {
    await api.patch('/users/me', {
      home_latitude: latitude,
      home_longitude: longitude,
      home_address: address,
    });
  }

  /**
   * Get unclaimed sites within radius of the current inspector's home location
   */
  async getUnclaimedNearbySites(
    radiusMiles: number = 25
  ): Promise<OpenSitesResponse> {
    const response = await api.get('/sites/unclaimed-nearby', {
      params: { radius_miles: radiusMiles },
    });
    return response.data as OpenSitesResponse;
  }

  /**
   * Claim an unclaimed site for the current inspector
   */
  async claimSite(siteId: string): Promise<ClaimSiteResult> {
    const response = await api.post(`/sites/${siteId}/claim`);
    return response.data as ClaimSiteResult;
  }

  /**
   * Release a site I own back to the group pool (no longer mine)
   */
  async unclaimSite(siteId: string): Promise<void> {
    await api.post(`/sites/${siteId}/unclaim`);
  }

  /**
   * The current inspector's My Sites (assigned) + Group Sites (claimable),
   * both from the single access resolver.
   */
  async getMyAccess(): Promise<MyAccessResponse> {
    const res = await api.get<MyAccessResponse>('/sites/my-access');
    return res.data;
  }
  /**
   * Analyze inspector-site patterns for auto-assignment recommendations
   */
  async analyzeAutoAssignments(
    lookback: number = 3,
    minConfidence: number = 0.67
  ): Promise<AutoAssignmentSummary> {
    const response = await api.get('/schedules/auto-assign/analyze', {
      params: { lookback, min_confidence: minConfidence },
    });
    return response.data as AutoAssignmentSummary;
  }

  /**
   * Apply auto-assignment recommendations
   */
  async applyAutoAssignments(
    request: AutoAssignmentApplyRequest
  ): Promise<AutoAssignmentApplyResult> {
    const response = await api.post('/schedules/auto-assign/apply', request);
    return response.data as AutoAssignmentApplyResult;
  }

  // Weekly Plan methods

  async getWeeklyPlan(
    weekDate?: string,
    inspectorId?: string
  ): Promise<WeeklyPlan> {
    const params = new URLSearchParams();
    if (weekDate) params.append('week_date', weekDate);
    // Manager+ only: view another inspector's plan (admin map drill-down).
    if (inspectorId) params.append('inspector_id', inspectorId);
    const qs = params.toString();
    const response = await api.get<WeeklyPlan>(
      `/schedules/weekly/plan${qs ? `?${qs}` : ''}`
    );
    return response.data;
  }

  async rescheduleInspection(
    inspectionId: string,
    newDate: string,
    everyWeek = false
  ): Promise<void> {
    await api.patch(
      `/schedules/weekly/plan/${inspectionId}?new_date=${newDate}` +
        (everyWeek ? '&every_week=true' : '')
    );
  }

  /** JIM-1006-04b: a day's planned inspections in the order they will be driven. */
  async setDayOrder(inspectionIds: string[]): Promise<void> {
    await api.post('/schedules/weekly/plan/order', {
      inspection_ids: inspectionIds,
    });
  }

  /** JIM-1014-14: the inspector commits to the week as planned. */
  async confirmWeek(
    weekDate: string
  ): Promise<{ confirmed: number; confirmed_at: string; week_start: string }> {
    const response = await api.post<{
      confirmed: number;
      confirmed_at: string;
      week_start: string;
    }>(`/schedules/weekly/plan/confirm?week_date=${weekDate}`);
    return response.data;
  }

  async removeFromPlan(inspectionId: string): Promise<void> {
    await api.delete(`/schedules/weekly/plan/${inspectionId}`);
  }

  async addToPlan(siteId: string, scheduledDate: string): Promise<void> {
    await api.post(
      `/schedules/weekly/plan?site_id=${siteId}&scheduled_date=${scheduledDate}`
    );
  }

  async buildMyPlan(weekStart?: string): Promise<{
    success: boolean;
    week_start: string;
    created: number;
    skipped: number;
    total_sites_due: number;
  }> {
    const params = weekStart ? `?week_start=${weekStart}` : '';
    const res = await api.post<{
      success: boolean;
      week_start: string;
      created: number;
      skipped: number;
      total_sites_due: number;
    }>(`/schedules/weekly/plan/build${params}`);
    return res.data;
  }
}

export type InspectorSiteRecommendation = {
  site_id: string;
  site_name: string;
  recommended_inspector_id: string;
  recommended_inspector_name: string;
  current_assigned_inspector_id: string | null;
  current_assigned_inspector_name: string | null;
  confidence: number;
  recent_inspection_count: number;
  total_inspections_analyzed: number;
  reason: string;
};

export type AutoAssignmentSummary = {
  total_sites_analyzed: number;
  sites_with_consistent_inspector: number;
  sites_needing_assignment: number;
  sites_with_stale_assignment: number;
  sites_already_correct: number;
  recommendations: InspectorSiteRecommendation[];
};

export type AutoAssignmentApplyRequest = {
  site_ids?: string[] | null;
  min_confidence?: number;
  dry_run?: boolean;
};

export type AutoAssignmentApplyResult = {
  applied: number;
  skipped: number;
  errors: string[];
  changes: Array<{
    site_id: string;
    site_name: string;
    previous_inspector_id: string | null;
    previous_inspector_name: string | null;
    new_inspector_id: string;
    new_inspector_name: string;
    confidence: number;
  }>;
};

// Weekly Plan Types

export type WeeklyPlanSite = {
  inspection_id: string;
  site_id: string;
  site_name: string;
  client_name?: string | null;
  project_id: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  scheduled_date: string;
  inspection_type: string;
  source: 'auto_scheduled' | 'manually_added' | 'delegated';
  delegated_from: string | null;
  route_order: number | null;
  /** JIM-1014-14: set when the inspector confirmed the week or touched this visit. */
  confirmed_at: string | null;
  /** JIM-1023-14: a visit filed on this day; shown as Completed, sorted last. */
  completed?: boolean;
};

export type WeeklyPlanDay = {
  date: string;
  day_of_week: string;
  sites: WeeklyPlanSite[];
  total_sites: number;
};

export type UnscheduledSite = {
  site_id: string;
  site_name: string;
  project_id: string;
  days_overdue: number;
  priority: number;
  inspection_frequency: string;
};

export type WeeklyPlan = {
  inspector_id: string;
  inspector_name: string;
  week_start: string;
  week_end: string;
  days: WeeklyPlanDay[];
  unscheduled_sites: UnscheduledSite[];
  summary: {
    total_auto_scheduled: number;
    total_manually_added: number;
    total_delegated: number;
    total_sites: number;
    total_confirmed: number;
    unscheduled_count: number;
  };
};

export const scheduleService = new ScheduleService();
